/**
 * Multi-Monitor Window Management
 *
 * Uses the Multi-Screen Window Placement API (Chrome/Edge, HTTPS only)
 * to detect screens and open viewer windows on specific monitors.
 *
 * Fallback for HTTP: opens windows that user can drag to other monitors.
 */

export interface ScreenInfo {
  label: string;
  left: number;
  top: number;
  width: number;
  height: number;
  isPrimary: boolean;
}

let screenDetails: any = null;
let permissionGranted = false;

/** Check if multi-screen API is available (requires HTTPS) */
export function isMultiScreenSupported(): boolean {
  return 'getScreenDetails' in window;
}

/** Request permission and get screen details */
export async function getScreens(): Promise<ScreenInfo[]> {
  if (isMultiScreenSupported()) {
    try {
      screenDetails = await (window as any).getScreenDetails();
      permissionGranted = true;

      return screenDetails.screens.map((s: any, i: number) => ({
        label: s.label || `Monitor ${i + 1}`,
        left: s.availLeft ?? s.left,
        top: s.availTop ?? s.top,
        width: s.availWidth ?? s.width,
        height: s.availHeight ?? s.height,
        isPrimary: s.isPrimary ?? (i === 0),
      }));
    } catch {
      // Permission denied — fall through to fallback
    }
  }

  // Fallback: we can't detect screens, but still allow opening new windows
  return [{
    label: 'This screen',
    left: 0,
    top: 0,
    width: window.screen.availWidth,
    height: window.screen.availHeight,
    isPrimary: true,
  }];
}

/** Track opened windows */
const openWindows = new Map<string, Window>();

/**
 * Open a new viewer window.
 * If screen API is available, positions on the target screen.
 * Otherwise opens a regular window the user can drag.
 */
export function openNewViewerWindow(
  studyId?: number,
  seriesId?: number,
  targetScreen?: ScreenInfo
): Window | null {
  // Derive base from current URL, not import.meta.env.BASE_URL. Vite's
  // `base: './'` (needed for portable sub-path deploys) makes BASE_URL
  // `'./'`, which glued to origin produces `http://server./` — trailing-
  // dot invalid hostname. Same pattern as getServerUrl / viewerUrl.
  const origin = window.location.origin;
  const path = window.location.pathname
    .replace(/\/+$/, '')
    .replace(/\/[^/]*\.html?$/i, '');
  let url = `${origin}${path}/`;
  if (studyId) {
    url += seriesId
      ? `#/viewer/${studyId}?series=${seriesId}`
      : `#/viewer/${studyId}`;
  } else {
    url += '#/viewer-empty';
  }

  const windowName = `edgeweb-viewer-${Date.now()}`;

  let features: string;
  if (targetScreen && permissionGranted) {
    features = [
      `left=${targetScreen.left}`,
      `top=${targetScreen.top}`,
      `width=${targetScreen.width}`,
      `height=${targetScreen.height}`,
    ].join(',');
  } else {
    // Fallback: open a large window, user drags it
    features = `width=${window.screen.availWidth},height=${window.screen.availHeight}`;
  }

  const win = window.open(url, windowName, features);
  if (win) {
    openWindows.set(windowName, win);

    // Try fullscreen on target screen
    if (targetScreen && permissionGranted && screenDetails) {
      try {
        const target = screenDetails.screens.find(
          (s: any) => (s.availLeft ?? s.left) === targetScreen.left &&
                       (s.availTop ?? s.top) === targetScreen.top
        );
        if (target) {
          win.addEventListener('load', () => {
            try {
              (win.document.documentElement as any).requestFullscreen?.({ screen: target });
            } catch { /* not critical */ }
          });
        }
      } catch { /* ignore */ }
    }

    // Clean up on close
    const check = setInterval(() => {
      if (win.closed) {
        openWindows.delete(windowName);
        clearInterval(check);
      }
    }, 2000);
  }

  return win;
}

/**
 * Open viewers on all non-primary screens (or one fallback window).
 */
export async function openOnAllScreens(studyId?: number): Promise<void> {
  const screens = await getScreens();
  const secondary = screens.filter((s) => !s.isPrimary);

  if (secondary.length > 0) {
    secondary.forEach((s) => openNewViewerWindow(studyId, undefined, s));
  } else {
    // No screen API — just open one window
    openNewViewerWindow(studyId);
  }
}

/** Close all opened viewer windows */
export function closeAllViewerWindows() {
  for (const [key, win] of openWindows.entries()) {
    if (!win.closed) win.close();
    openWindows.delete(key);
  }
}
